import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { PublicLayout } from '../components/layout/PublicLayout'
import { ChurchQuickFind } from '../components/ChurchQuickFind'
import { buildForChurchesMeta } from '@fellowship/shared'

const pageMeta = buildForChurchesMeta()
import { ScanLine, Layers, Zap, UtensilsCrossed, Link2, Smartphone, Check } from 'lucide-react'

const BENEFITS = [
  {
    icon: ScanLine,
    title: 'Scan your bulletin, we\'ll import every event',
    body: 'Snap a photo of this week\'s bulletin, or paste your Google Calendar link — we\'ll pull in every event automatically. No manual re-entry, no double data entry.',
  },
  {
    icon: Layers,
    title: 'Stop managing five different places',
    body: 'Bulletin, calendar, potluck sign-up sheet, group chat, church website. Fellowship replaces the pile of tools with one link your whole congregation already has saved: their phone.',
  },
  {
    icon: Zap,
    title: 'Two-minute setup',
    body: 'Nothing to install. Nothing to maintain. Claim your listing, connect your calendar, and you\'re done before your coffee gets cold.',
  },
]

const LOVES = [
  {
    icon: UtensilsCrossed,
    title: 'Potluck sign-ups that fit your event',
    body: 'Doing a baked-potato bar instead of the usual mains-and-sides spread? Add your own categories — "Toppings," "Butter & Sour Cream," whatever you need. RSVPs tell you who\'s coming, and you can send everyone a quick update if plans change.',
  },
  {
    icon: Link2,
    title: 'One link, not five',
    body: 'Your bulletin, calendar, and potluck sign-up all live at one address you can text, print, or paste into an email. Nobody has to remember which app has which.',
  },
  {
    icon: Smartphone,
    title: 'Works on the phone people already have',
    body: 'No app to download, no login to reset. It opens like any web page — on the phone your whole congregation is already carrying.',
  },
]

const STEPS = [
  'Find your church in our directory, or add it if it\'s not there yet',
  'Snap a photo of your bulletin, or connect your Google Calendar',
  'Share the one link with your people',
  'Done — no ongoing upkeep',
]

export default function ForChurchesPage() {
  return (
    <>
      <Helmet>
        <title>{pageMeta.title}</title>
        <link rel="canonical" href={pageMeta.canonicalUrl} />
        {pageMeta.metaTags.map((tag, i) =>
          tag.property ? (
            <meta key={i} property={tag.property} content={tag.content} />
          ) : (
            <meta key={i} name={tag.name} content={tag.content} />
          ),
        )}
        <script type="application/ld+json">{JSON.stringify(pageMeta.jsonLd)}</script>
      </Helmet>
    <PublicLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="max-w-[1080px] mx-auto px-6 pt-16 pb-12">
        <div className="max-w-[640px]">
          <div className="inline-block text-[11px] font-semibold uppercase tracking-[0.1em] text-primary bg-primary-soft border border-primary rounded-full px-3 py-1 mb-5">
            For churches
          </div>
          <h1 className="serif text-[46px] leading-[1.12] tracking-[-0.025em] text-ink m-0 mb-5">
            One place for<br />
            <span className="serif-italic text-primary">everything your church does.</span>
          </h1>
          <p className="text-[17px] text-ink-2 leading-[1.65] m-0 mb-8 max-w-[540px]">
            Your events live in Google Calendar. Your potluck sign-up is a spreadsheet. Your bulletin is a PDF nobody opens. Your group chat is buried in Facebook. We bring it all into one link — set up in about two minutes.
          </p>
          <ChurchQuickFind className="max-w-[420px]" />
          <p className="text-[12.5px] text-muted mt-3">
            Two-minute setup &middot; Nothing to install &middot; No credit card required &middot;{' '}
            <Link to="/churches/new" className="text-ink-2 font-medium">
              add your church
            </Link>{' '}
            if it's not listed
          </p>
        </div>
      </div>

      {/* ── Why Fellowship ────────────────────────────────────────────────── */}
      <div className="bg-s2 border-t border-b border-line-soft py-14">
        <div className="max-w-[1080px] mx-auto px-6">
          <div className="eyebrow text-center mb-10">Why Fellowship</div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {BENEFITS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-surface border border-line-soft rounded-2xl p-6">
                <div className="w-10 h-10 rounded-xl bg-primary-soft flex items-center justify-center mb-4">
                  <Icon size={18} className="text-primary" />
                </div>
                <div className="text-[15px] font-semibold text-ink mb-2 leading-[1.3]">{title}</div>
                <p className="text-[13px] text-ink-2 leading-[1.6] m-0">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── What small churches love ─────────────────────────────────────── */}
      <div className="border-b border-line-soft py-14">
        <div className="max-w-[1080px] mx-auto px-6">
          <div className="eyebrow text-center mb-10">What small churches love</div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {LOVES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-surface border border-line-soft rounded-2xl p-6">
                <div className="w-10 h-10 rounded-xl bg-primary-soft flex items-center justify-center mb-4">
                  <Icon size={18} className="text-primary" />
                </div>
                <div className="text-[15px] font-semibold text-ink mb-2 leading-[1.3]">{title}</div>
                <p className="text-[13px] text-ink-2 leading-[1.6] m-0">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── How it works ──────────────────────────────────────────────────── */}
      <div className="max-w-[680px] mx-auto px-6 py-14">
        <div className="eyebrow text-center mb-8">Here's what you actually do</div>
        <div className="flex flex-col gap-4">
          {STEPS.map((step, i) => (
            <div key={i} className="flex gap-3 items-start">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-on-primary text-[11px] font-bold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <p className="text-[14px] text-ink-2 leading-[1.6] m-0">{step}</p>
            </div>
          ))}
        </div>

        {/* ── CTA card ────────────────────────────────────────────────────── */}
        <div className="mt-10 rounded-2xl bg-primary-soft border border-primary p-7 text-center">
          <h2 className="serif text-[26px] text-ink m-0 mb-2">Ready to stop juggling apps?</h2>
          <p className="text-[14px] text-ink-2 m-0 mb-5">
            We may already have a listing for your church. Search to find out.
          </p>
          <ChurchQuickFind className="max-w-[420px] mx-auto text-left" />
        </div>

        {/* ── Trust line ──────────────────────────────────────────────────── */}
        <div className="mt-8 flex flex-col gap-2">
          {['Free to claim your listing', 'Two-minute setup — no credit card required', '14-day free trial, cancel anytime'].map((line) => (
            <div key={line} className="flex items-center gap-2 text-[13px] text-ink-2">
              <Check size={14} className="text-primary shrink-0" />
              {line}
            </div>
          ))}
        </div>
      </div>
    </PublicLayout>
    </>
  )
}
